import React, { useEffect, useState } from "react";
import { getShifts } from "../../../_api/EventShift";
import { Button } from "../../../_helpers/components/Button";
import { InputForm } from "../../../_helpers/components/Forms/InputLabel";
import { useOutletContext } from "react-router-dom";
import { updateUserPhone } from "../../../_api/User";
import toast from "react-hot-toast";

const dateTimeFormatter = new Intl.DateTimeFormat("fr-FR", {
    weekday: "long",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
});

const timeFormatter = new Intl.DateTimeFormat("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
});

const formatDateTime = (date) => dateTimeFormatter.format(new Date(date));

const formatTime = (date) => timeFormatter.format(new Date(date));

const getDurationInMinutes = (start, end) =>
    Math.floor((new Date(end).getTime() - new Date(start).getTime()) / 60000);

const formatDuration = (start, end) => {
    const durationInMinutes = getDurationInMinutes(start, end);

    if (durationInMinutes < 60) {
        return `${durationInMinutes} min`;
    }

    const hours = Math.floor(durationInMinutes / 60);
    const minutes = durationInMinutes % 60;

    if (minutes === 0) {
        return `${hours} h`;
    }

    return `${hours} h ${minutes} min`;
};

const mapper = {
    PENDING: "En attente",
    ACCEPTED: "Accepté",
    REFUSED: "Non retenu",
    WITHDRAWAL: "Désistement",
    WAITING_LIST: "Liste d'attente",
};

const colorMapper = {
    PENDING: "text-blue-600",
    ACCEPTED: "text-green-600",
    REFUSED: "text-red-600",
    WITHDRAWAL: "text-red-600",
    WAITING_LIST: "text-yellow-600",
};

export const Participation = () => {
    const [user, setUser, currentApp, currentMenu] = useOutletContext();
    const [statusFilter, setStatusFilter] = useState("Tous");
    const [currentPage, setCurrentPage] = useState(1);
    const [participations, setParticipations] = useState([]);
    const [orderBy, setOrderBy] = useState("-DATE");
    const [phone_1, setPhone_1] = useState(user?.phone_1 || "");
    const [phone_2, setPhone_2] = useState(user?.phone_2 || "");
    const [isSaving, setIsSaving] = useState(false);

    const rowsPerPage = 15;

    const labelType = {
        INTERNAL: "Interne",
        PRIVATE: "Privé",
        PUBLIC: "Public",
        MECENAT: "Mécénat",
        OVERTIME: "H. supp.",
    };

    const updatePhone = () => {
        setIsSaving(true);
        updateUserPhone({
            phone_1: phone_1,
            phone_2: phone_2,
        }, user.id)
            .then((resp) => {
                toast.success(resp.data.message);
                setUser({ ...user, phone_1: phone_1, phone_2: phone_2 });
            })
            .finally(() => setIsSaving(false));
    }

    const sortedBy = ((anArray) => {
        const sortedArray = [...anArray];

        sortedArray.sort((a, b) => a.start <= b.start ? 1 : -1);

        switch (orderBy) {
            case "DATE":
                sortedArray.sort((a, b) => a.start <= b.start ? -1 : 1);
                break;
            case "-DATE":
                sortedArray.sort((a, b) => a.start >= b.start ? -1 : 1);
                break;
            case "TITLE":
                sortedArray.sort((a, b) => a.title.toLowerCase() <= b.title.toLowerCase() ? -1 : 1);
                break;
            case "-TITLE":
                sortedArray.sort((a, b) => a.title.toLowerCase() >= b.title.toLowerCase() ? -1 : 1);
                break;
            case "TYPE":
                sortedArray.sort((a, b) => a.type <= b.type ? -1 : 1);
                break;
            case "-TYPE":
                sortedArray.sort((a, b) => a.type >= b.type ? -1 : 1);
                break;
            case "DURATION":
                sortedArray.sort((a, b) => getDurationInMinutes(a.start, a.end) <= getDurationInMinutes(b.start, b.end) ? -1 : 1);
                break;
            case "-DURATION":
                sortedArray.sort((a, b) => getDurationInMinutes(a.start, a.end) >= getDurationInMinutes(b.start, b.end) ? -1 : 1);
                break;
            case "STATUS":
                sortedArray.sort((a, b) => a.status <= b.status ? -1 : 1);
                break;
            case "-STATUS":
                sortedArray.sort((a, b) => a.status >= b.status ? -1 : 1);
                break;
            default:
                sortedArray.sort((a, b) => a.start <= b.start ? 1 : -1);
        }
        return sortedArray;
    })

    useEffect(() => {
        getShifts().then((resp) => {
            const formatted = resp.data.map((shift) => ({
                id: shift.id,
                start: shift.start_time,
                end: shift.end_time,
                title: shift.event.name,
                type: shift.event?.type ? labelType[shift.event.type] : "-", // ou autre champ pour "Type"
                status: shift.pivot?.status || "—",
            }));
            setParticipations(sortedBy(formatted));
        });
    }, []);

    const filteredRows =
        statusFilter === "Tous"
            ? participations
            : participations.filter((p) => p.status === statusFilter);

    const totalPages = Math.ceil(filteredRows.length / rowsPerPage);
    const paginatedRows = filteredRows.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
    );

    useEffect(() => {
        setParticipations(sortedBy(participations));
    }, [orderBy]);

    const setOrder = ((val) => {
        switch (val) {
            case "DATE":
                setOrderBy(orderBy === "DATE" ? "-DATE" : "DATE");
                break;
            case "-DATE":
                setOrderBy("-DATE");
                break;
            case "TITLE":
                setOrderBy(orderBy === "TITLE" ? "-TITLE" : "TITLE");
                break;
            case "-TITLE":
                setOrderBy("-TITLE");
                break;
            case "TYPE":
                setOrderBy(orderBy === "TYPE" ? "-TYPE" : "TYPE");
                break;
            case "-TYPE":
                setOrderBy("-TYPE");
                break;
            case "DURATION":
                setOrderBy(orderBy === "DURATION" ? "-DURATION" : "DURATION");
                break;
            case "-DURATION":
                setOrderBy("-DURATION");
                break;
            case "STATUS":
                setOrderBy(orderBy === "STATUS" ? "-STATUS" : "STATUS");
                break;
            case "-STATUS":
                setOrderBy("-STATUS");
                break;
            default:
                setOrderBy("-DATE");
        }
    })

    const handleFilterChange = (e) => {
        setStatusFilter(e.target.value);
        setCurrentPage(1); // reset page when filter changes
    };

    return (
        <div className="p-4">

            <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                <div className="mb-4 flex flex-col gap-3 bg-cyan-50 min-w-1/3 p-4 rounded-lg border border-cyan-700 text-cyan-800">
                    <p className="text-sm">Vous pouvez ici renseigner votre numéro de téléphone pour pouvoir être appelé en cas de remplacement ou de désistement.</p>
                    <div className="flex gap-4">
                        <InputForm type="text" value={phone_1} placeholder="Téléphone 1" set={setPhone_1} />
                        <InputForm type="text" value={phone_2} placeholder="Téléphone 2" set={setPhone_2} />
                        <Button
                            name="Enregistrer"
                            action={() => {
                                updatePhone();
                            }}
                            type="custom"
                            loading={isSaving}
                            customCN="h-10 px-4 cursor-pointer rounded-sm border-2 border-app-primary bg-app-primary hover:border-app-secondary hover:bg-app-secondary text-white"
                        />
                    </div>

                </div>

                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                    <label htmlFor="statusFilter" className="font-medium whitespace-nowrap">
                        Filtrer par statut :
                    </label>
                    <select
                        id="statusFilter"
                        value={statusFilter}
                        onChange={handleFilterChange}
                        className="border rounded-sm px-2 py-2 text-sm bg-white"
                    >
                        <option value="Tous">Tous</option>
                        <option value="PENDING">En attente</option>
                        <option value="WAITING_LIST">Liste d'attente</option>
                        <option value="ACCEPTED">Accepté</option>
                        <option value="REFUSED">Non retenu</option>
                        <option value="WITHDRAWAL">Désistement</option>
                    </select>
                </div>

                {/* Tri (mobile) */}
                <div className="lg:hidden flex flex-col gap-2">
                    <label htmlFor="orderSelect" className="font-medium whitespace-nowrap">
                        Trier par :
                    </label>
                    <select
                        id="orderSelect"
                        value={orderBy}
                        onChange={(e) => setOrder(e.target.value)}
                        className="border rounded-sm px-2 py-2 text-sm bg-white"
                    >
                        <option value="-DATE">Date (récent → ancien)</option>
                        <option value="DATE">Date (ancien → récent)</option>
                        <option value="TITLE">Manifestation (A → Z)</option>
                        <option value="-TITLE">Manifestation (Z → A)</option>
                        <option value="TYPE">Type (A → Z)</option>
                        <option value="-TYPE">Type (Z → A)</option>
                        <option value="DURATION">Durée (court → long)</option>
                        <option value="-DURATION">Durée (long → court)</option>
                        <option value="STATUS">État (A → Z)</option>
                        <option value="-STATUS">État (Z → A)</option>
                    </select>
                </div>
            </div>

            {/* Mobile: cards */}
            <div className="lg:hidden space-y-3">
                {paginatedRows.length > 0 ? (
                    paginatedRows.map((row) => {
                        const statusLabel = mapper[row.status] ?? row.status ?? "—";
                        const statusColor = colorMapper[row.status] ?? "text-gray-600";
                        return (
                            <div key={row.id} className="border rounded-lg bg-white shadow-sm p-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="font-semibold text-gray-900 truncate">
                                            {row.title}
                                        </div>
                                        <div className="text-xs text-gray-600 mt-1">
                                            {formatDateTime(row.start)} – {formatTime(row.end)}
                                        </div>
                                    </div>
                                    <div className={`text-sm font-semibold whitespace-nowrap ${statusColor}`}>
                                        {statusLabel}
                                    </div>
                                </div>

                                <div className="mt-3 flex flex-wrap gap-2">
                                    <span className="text-xs px-2 py-1 rounded-full border bg-gray-50 text-gray-700">
                                        {row.type}
                                    </span>
                                    <span className="text-xs px-2 py-1 rounded-full border bg-gray-50 text-gray-700">
                                        {formatDuration(row.start, row.end)}
                                    </span>
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div className="text-center text-gray-500 p-4 border rounded-lg bg-white">
                        Aucune participation avec ce statut.
                    </div>
                )}
            </div>

            {/* Desktop: table */}
            <table className="hidden lg:table min-w-full table-auto border rounded-sm overflow-hidden shadow-sm text-sm">
                <thead className="bg-gray-100 text-gray-700">
                    <tr>
                        <th className="px-4 py-2 text-left"><span onClick={() => setOrder("DATE")} className="cursor-pointer hover:underline">Date</span></th>
                        <th className="px-4 py-2 text-left"><span onClick={() => setOrder("TITLE")} className="cursor-pointer hover:underline">Manifestation</span></th>
                        <th className="px-4 py-2 text-left"><span onClick={() => setOrder("TYPE")} className="cursor-pointer hover:underline">Type</span></th>
                        <th className="px-4 py-2 text-left"><span onClick={() => setOrder("DURATION")} className="cursor-pointer hover:underline">Durée</span></th>
                        <th className="px-4 py-2 text-left"><span onClick={() => setOrder("STATUS")} className="cursor-pointer hover:underline">Etat</span></th>
                    </tr>
                </thead>
                <tbody>
                    {paginatedRows.length > 0 ? (
                        paginatedRows.map((row, index) => (
                            <tr
                                key={index}
                                className="border-t hover:bg-gray-50 transition"
                            >
                                <td className="px-4 py-2">
                                    {formatDateTime(row.start)} - {formatTime(row.end)}
                                </td>
                                <td className="px-4 py-2">{row.title}</td>
                                <td className="px-4 py-2">{row.type}</td>
                                <td className="px-4 py-2">
                                    {formatDuration(row.start, row.end)}
                                </td>
                                <td className="px-4 py-2">
                                    <span
                                        className={colorMapper[row.status] ?? "text-gray-600"}
                                    >
                                        {mapper[row.status] ?? row.status ?? "—"}
                                    </span>
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td
                                colSpan="5"
                                className="text-center text-gray-500 p-4"
                            >
                                Aucune participation avec ce statut.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>

            {/* Pagination controls */}
            {totalPages > 1 && (
                <div className="mt-4 flex justify-center items-center gap-4 text-sm">
                    <button
                        onClick={() =>
                            setCurrentPage((p) => Math.max(1, p - 1))
                        }
                        disabled={currentPage === 1}
                        className="px-3 py-1 border rounded-sm disabled:opacity-50"
                    >
                        Précédent
                    </button>
                    <span>
                        Page {currentPage} sur {totalPages}
                    </span>
                    <button
                        onClick={() =>
                            setCurrentPage((p) => Math.min(totalPages, p + 1))
                        }
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 border rounded-sm disabled:opacity-50"
                    >
                        Suivant
                    </button>
                </div>
            )}
        </div>
    );
};
